import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Loader2, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { checkAuthSecurity } from '@/utils/authSecurity';
import { ConnectionStatus } from '@/components/auth/ConnectionStatus';
import { useAuth } from '@/components/AuthProvider';
import { FieldError } from '@/components/auth/FieldError';
import { captureUTMParams } from '@/utils/marketing';

export default function Auth() {
  const { user, loading: authLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  // Scroll to top on mount and capture UTM params
  useEffect(() => {
    window.scrollTo(0, 0);
    captureUTMParams(); // Capture UTMs from URL for marketing tracking
  }, []);

  // Redirect authenticated users to home
  useEffect(() => {
    if (!authLoading && user) {
      navigate('/', { replace: true });
    }
  }, [user, authLoading, navigate]);

  // Check cooldown on mount and update countdown
  useEffect(() => {
    const checkCooldown = () => {
      const lastSent = localStorage.getItem('last_auth_otp_sent_time');
      if (lastSent) {
        const timePassed = Date.now() - parseInt(lastSent);
        const remainingSeconds = Math.ceil((60000 - timePassed) / 1000);
        if (remainingSeconds > 0) {
          setCooldownSeconds(remainingSeconds);
        } else {
          setCooldownSeconds(0);
        }
      }
    };

    checkCooldown();
    const interval = setInterval(checkCooldown, 1000);
    return () => clearInterval(interval);
  }, []);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = emailRegex.test(email) && email.trim().length > 0;
    
    if (!isValid) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
    
    return isValid;
  };

  const checkSecurity = async (attemptType: 'signin' | 'signup') => {
    const check = await checkAuthSecurity(email.trim(), attemptType);
    
    if (!check.canProceed && check.message) {
      toast({
        title: "Too Many Attempts",
        description: check.message,
        variant: "destructive"
      });
      return false;
    }
    
    return true;
  };

  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateEmail(email)) {
      return;
    }

    // Check rate limit - prevent sending another code within 60 seconds
    const lastSent = localStorage.getItem('last_auth_otp_sent_time');
    if (lastSent) {
      const timePassed = Date.now() - parseInt(lastSent);
      if (timePassed < 60000) {
        const remainingSeconds = Math.ceil((60000 - timePassed) / 1000);
        toast({
          title: "Please wait",
          description: `You can request a new code in ${remainingSeconds} seconds.`,
          variant: "destructive"
        });
        return;
      }
    }

    setLoading(true);

    try {
      // Check security before proceeding
      const securityCheck = await checkSecurity('signin');
      if (!securityCheck) {
        setLoading(false);
        return;
      }

      // First, try to sign in without creating user to check if they exist
      const { error: signInError } = await supabase.auth.signInWithOtp({
        email: email.trim(),
        options: {
          shouldCreateUser: false,
        }
      });

      // If error, determine if it's because user doesn't exist or signups are required
      if (signInError) {
        const msg = String(signInError.message || '').toLowerCase();
        const needsSignup =
          msg.includes('not found') ||
          msg.includes('sign in existing users') ||
          msg.includes('signup first') ||
          msg.includes('signups not allowed for otp') ||
          msg.includes('otp_disabled');

        if (needsSignup) {
          navigate('/auth/signup', {
            state: { email: email.trim() },
            replace: true
          });
          setLoading(false);
          return;
        }

        // Unknown error -> surface to user
        console.error('Sign-in OTP error:', signInError);
        throw signInError;
      }

      // OTP sent for existing user — proceed to verification
      // Store timestamp to enforce rate limit
      localStorage.setItem('last_auth_otp_sent_time', Date.now().toString());
      setCooldownSeconds(60);

      // Store email for OTP verification page
      localStorage.setItem('pending_auth_email', email);
      
      toast({
        title: "Check your email",
        description: "We've sent you a verification code.",
      });
      
      // Navigate to OTP verification page
      navigate('/auth/verify-otp', { 
        state: { email },
        replace: true 
      });
    } catch (error: any) {
      console.error('Continue error:', error);
      toast({
        title: "Error",
        description: error?.message || "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-background">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Don't render if user is authenticated
  if (user) {
    return null;
  }

  return (
    <>
      <ConnectionStatus />
      <div 
        className="h-screen relative overflow-hidden bg-background pt-safe"
        style={{
          backgroundImage: 'url(https://hwjejrsktuenskryvsvl.supabase.co/storage/v1/object/public/hero-banners/New%20Authbackground/AUTHBACKGROUND%20copy.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Main Content */}
        <div className="relative z-10 h-full flex flex-col items-center justify-center p-4 overflow-hidden">

          {/* Email Capture Form */}
          <motion.div
            initial={{ y: 30 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-md sm:max-w-md md:max-w-2xl mx-auto mb-8"
          >
            <div className="glass-morphism relative rounded-lg overflow-hidden">
              {/* Floating close button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                className="absolute top-2 left-2 z-10 w-10 h-10 p-0 rounded-full bg-white/10 backdrop-blur-xl border-0 hover:bg-white/20 transition-all duration-200 hover:scale-105"
              >
                <X className="w-4 h-4 text-foreground" />
              </Button>
              
              <div className="relative px-4 sm:px-6 py-12 sm:py-16 flex flex-col items-center justify-center min-h-full sm:min-h-auto w-full max-w-full overflow-hidden">
                <div className="space-y-6 w-full max-w-md">
                  <div className="text-center space-y-4">
                    <div className="flex justify-center mb-4">
                      <div className="p-1.5 rounded-3xl bg-white/10 backdrop-blur-xl border border-white/20 shadow-2xl drop-shadow-lg">
                        <img 
                          src="https://hwjejrsktuenskryvsvl.supabase.co/storage/v1/object/public/applogos/thicker_.png"
                          alt="BuxBox Logo"
                          className="h-16 w-auto"
                        />
                      </div>
                    </div>
                    <h2 className="text-3xl font-semibold text-foreground tracking-tight mb-4" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif' }}>
                      Continue with Email
                    </h2>
                    <p className="text-foreground/80 mt-4" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif' }}>
                      You can sign in if you already have an account, or we'll help you create one.
                    </p>
                  </div>

                  <form onSubmit={handleContinue} className="space-y-4">
                    <div>
                      <Input
                        type="email"
                        placeholder="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full h-12 bg-white/10 border-0 backdrop-blur-xl focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none transition-colors text-base text-foreground placeholder:text-foreground/60"
                        style={{ 
                          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif',
                          borderRadius: '14px / 18px'
                        }}
                        disabled={loading}
                      />
                      {emailError && <FieldError error={emailError} />}
                    </div>

                    {/* Peer to Peer Icon */}
                    <div className="flex justify-center mt-16 -mb-6">
                      <img 
                        src="https://hwjejrsktuenskryvsvl.supabase.co/storage/v1/object/public/applogos/Icons%20custom/handshake_glass_icon_light.webp"
                        alt="Peer to peer exchange"
                        className="w-12 h-12"
                      />
                    </div>

                    {/* Legal Text */}
                    <p className="text-center text-xs text-foreground/70 leading-relaxed px-4 pb-3 pt-0 rounded-lg backdrop-blur-sm">
                      Your email address is used to securely send you a verification code. We do not store passwords or any sensitive data online to protect your account. By continuing, you agree to our Terms of Service and Privacy Policy.
                    </p>

                    <div className="flex justify-center">
                      <Button
                        type="submit"
                        className="w-3/4 h-14 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-semibold text-[17px] tracking-[-0.3px] shadow-[0_2px_6px_rgba(0,0,0,0.1)] px-6"
                        style={{ 
                          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif',
                          borderRadius: '14px / 18px'
                        }}
                        disabled={loading || !email.trim() || cooldownSeconds > 0}
                      >
                        {loading ? (
                          <div className="flex items-center gap-2">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Processing...
                          </div>
                        ) : cooldownSeconds > 0 ? (
                          <div className="flex items-center gap-2">
                            Wait {cooldownSeconds}s to resend
                          </div>
                        ) : (
                          'Continue'
                        )}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
}
